<template>
  <div class="relative overflow-x-auto bg-white border border-solid rounded-lg shadow-lg border-neutral-300">
    <!-- Calendar controls -->
    <div class="flex items-center justify-between m-1.5 px-1.5 w-full mx-auto">
      <div class="flex items-center space-x-1.5">
        <!-- Previous Period Button -->
        <button @click="previousPeriod"
          class="w-[40px] h-[40px] flex items-center p-2 rounded-lg text-neutral-800 bg-neutral-100 hover:bg-neutral-200 justify-center">
          <ChevronLeft :size="20" :stroke-width="2.25" />
        </button>
        <!-- Current Period Button -->
        <button @click="openModal"
          class="w-28 h-[40px] flex items-center p-2 rounded-lg text-neutral-800 bg-neutral-100 hover:bg-neutral-200 justify-center">
          <span class="text-sm font-medium text-black">
            {{ isYearlyView ? currentYear : `${currentMonth}/${currentYear}` }}
          </span>
        </button>
        <!-- Next Period Button -->
        <button @click="nextPeriod"
          class="w-[40px] h-[40px] flex items-center p-2 rounded-lg text-neutral-800 bg-neutral-100 hover:bg-neutral-200 justify-center">
          <ChevronRight :size="20" :stroke-width="2.25" />
        </button>
        <!-- Go to Today Button -->
        <button @click="goToToday"
          class="w-[40px] h-[40px] flex items-center p-2 2md:w-28 rounded-lg text-neutral-800 bg-neutral-100 hover:bg-neutral-200 justify-center text-sm">
          <ArrowDownToDot :size="20" class="-mt-0.5 block 2md:hidden" :stroke-width="1.75" />
          <span class="hidden text-sm font-medium 2md:inline">Gå til i dag</span>
        </button>
        <!-- Color coding info icon and label -->
        <div @mouseenter="showColorCodingInfo" @mouseleave="hideColorCodingInfo"
          class="items-center hidden py-2 ml-1 space-x-1 2xs:flex text-neutral-500">
          <Info :size="16" :stroke-width="1.75" />
          <span class="text-xs font-medium">Farvekodning</span>
        </div>
      </div>

      <!-- View toggle buttons -->
      <div class="flex items-center space-x-1.5">
        <!-- Yearly View Button -->
        <button @click="toggleView('yearly')"
          class="w-[40px] h-[40px] flex items-center sm:space-x-2 p-2 sm:pr-3 sm:w-28 rounded-lg justify-center text-sm"
          :disabled="isYearlyView" :class="{
            'text-white bg-neutral-800': isYearlyView,
            'text-neutral-800 bg-neutral-100 hover:bg-neutral-200': !isYearlyView,
          }">
          <Calendar :size="18" class="-mt-0.5" :stroke-width="1.75" />
          <span class="hidden text-sm font-medium sm:inline">Årlig</span>
        </button>
        <!-- Monthly View Button -->
        <button @click="toggleView('monthly')"
          class="w-[40px] h-[40px] flex items-center sm:space-x-2 p-2 sm:pr-3 sm:w-28 rounded-lg justify-center text-sm"
          :disabled="!isYearlyView" :class="{
            'text-white bg-neutral-800': !isYearlyView,
            'text-neutral-800 bg-neutral-100 hover:bg-neutral-200': isYearlyView,
          }">
          <CalendarDays :size="18" class="-mt-0.5" :stroke-width="1.75" />
          <span class="hidden text-sm font-medium sm:inline">Månedlig</span>
        </button>
      </div>
    </div>

    <!-- Calendar View Component -->
    <div class="relative overflow-x-auto bg-white border-t border-solid rounded-b-lg border-neutral-200">
      <div class="max-h-[calc(100vh-11rem)] overflow-y-auto" ref="calendarContainer">
        <component :is="currentCalendarView" :key="currentDateKey" :current-date="currentDate" :users="users"
          @show-user-info="showUserInfo" @hide-user-info="hideUserInfo" @show-event-info="showEventInfo"
          @hide-event-info="hideEventInfo" @edit-replacement="openReplacementModal" ref="calendarView" />
      </div>
    </div>

    <!-- User Info Popover -->
    <UserInfoPopover v-if="userInfo" :user="activeUser" :initial-x="infoX" :initial-y="infoY" @close="hideUserInfo"
      :is-visible="userInfo" />

    <!-- Event Info Popover -->
    <EventInfoPopover v-if="eventInfo" :event="activeEvent" :initial-x="eventInfoX" :initial-y="eventInfoY"
      @close="hideEventInfo" :is-visible="eventInfo" />

    <!-- Color Coding Popover -->
    <EventTypeColorsPopover v-if="colorCodingInfo" :initial-x="colorCodingInfoX" :initial-y="colorCodingInfoY"
      :is-visible="colorCodingInfo" @hide="hideColorCodingInfo" />

    <!-- Date Picker Modal -->
    <ReusableModal :isOpen="showModal" :isConfirm="true" @close="closeModal" @submit="applySelection"
      :title="modalTitle">
      <component :is="currentSelectionComponent" v-if="isYearlyView" v-model:selected-year="selectedYear" />
      <component :is="currentSelectionComponent" v-else v-model:selected-month="selectedMonth"
        v-model:selected-year="selectedYear" />
    </ReusableModal>

    <!-- Replacement Modal -->
    <ReusableModal :isOpen="showReplacementModal" :isConfirm="true" @close="closeReplacementModal"
      @submit="updateEventReplacement" title="Vælg en afløser">
      <ReplacementUserSelection v-model:selectedReplacementId="selectedReplacementId" :users="users" />
    </ReusableModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, markRaw, inject, defineProps, watch } from 'vue';
import { Calendar, CalendarDays, ChevronLeft, ChevronRight, ArrowDownToDot, Info } from 'lucide-vue-next';
import YearlyCalendarView from '@/components/ui/AdminDashboard/Views/YearlyCalendarView.vue';
import MonthlyCalendarView from '@/components/ui/AdminDashboard/Views/MonthlyCalendarView.vue';
import UserInfoPopover from '@/components/ui/AdminDashboard/Popovers/UserInfoPopover.vue';
import EventInfoPopover from '@/components/ui/AdminDashboard/Popovers/EventInfoPopover.vue';
import EventTypeColorsPopover from '@/components/ui/AdminDashboard/Popovers/EventTypeColorsPopover.vue';
import ReusableModal from '@/components/ui/ReusableModal.vue';
import MonthYearSelection from '@/components/ui/AdminDashboard/MonthYearSelection.vue';
import YearSelection from '@/components/ui/AdminDashboard/YearSelection.vue';
import ReplacementUserSelection from '@/components/ui/AdminDashboard/ReplacementUserSelection.vue';

defineProps({
  users: Array,
});

// Store injections
const eventsStore = inject('eventsStore');
const usersStore = inject('usersStore');

// Calendar state
const currentDate = ref(new Date());
const isYearlyView = ref(true);

// Popovers and modals state
const userInfo = ref(false);
const eventInfo = ref(false);
const colorCodingInfo = ref(false);
const showModal = ref(false);
const showReplacementModal = ref(false);

// Active items for popovers and modals
const activeUser = ref({});
const activeEvent = ref({});
const currentEvent = ref(null);
const selectedReplacementId = ref('');

// Popover positioning
const infoX = ref(0);
const infoY = ref(0);
const eventInfoX = ref(0);
const eventInfoY = ref(0);
const colorCodingInfoX = ref(0);
const colorCodingInfoY = ref(0);

// Modal state
const selectedMonth = ref(currentDate.value.getMonth());
const selectedYear = ref(currentDate.value.getFullYear());

// DOM refs
const calendarContainer = ref(null);
const calendarView = ref(null);

// Computed properties
const currentDateKey = computed(() => `${currentDate.value.getFullYear()}-${currentDate.value.getMonth()}`);
const currentYear = computed(() => currentDate.value.getFullYear());
const currentMonth = computed(() => {
  return new Intl.DateTimeFormat('da-DK', { month: '2-digit' }).format(currentDate.value);
});
const currentCalendarView = computed(() => isYearlyView.value ? markRaw(YearlyCalendarView) : markRaw(MonthlyCalendarView));
const currentSelectionComponent = computed(() => isYearlyView.value ? markRaw(YearSelection) : markRaw(MonthYearSelection));
const modalTitle = computed(() => isYearlyView.value ? 'Vælg år' : 'Vælg måned og år');

// Variables to track dragging state
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);

// Calendar navigation methods
function previousPeriod() {
  if (isYearlyView.value) {
    currentDate.value = new Date(currentYear.value - 1, 0, 1);
  } else {
    currentDate.value = new Date(currentYear.value, currentDate.value.getMonth() - 1, 1);
  }
}

function nextPeriod() {
  if (isYearlyView.value) {
    currentDate.value = new Date(currentYear.value + 1, 0, 1);
  } else {
    currentDate.value = new Date(currentYear.value, currentDate.value.getMonth() + 1, 1);
  }
}

function goToToday() {
  currentDate.value = new Date();
  nextTick(() => {
    scrollToCurrentDay();
  });
}

function toggleView(view) {
  isYearlyView.value = view === 'yearly';
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    isYearlyView.value ? 0 : currentDate.value.getMonth(),
    1
  );
  goToToday();
}

// Modal and popover methods
function openModal() {
  selectedMonth.value = currentDate.value.getMonth();
  selectedYear.value = currentDate.value.getFullYear();
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function applySelection() {
  if (isYearlyView.value) {
    currentDate.value = new Date(selectedYear.value, 0, 1);
  } else {
    currentDate.value = new Date(selectedYear.value, selectedMonth.value, 1);
  }
  closeModal();
}

function showUserInfo(user, x, y) {
  activeUser.value = user;
  userInfo.value = true;
  infoX.value = x;
  infoY.value = y;
}

function hideUserInfo() {
  userInfo.value = false;
}

function showEventInfo(event, x, y) {
  activeEvent.value = event;
  eventInfo.value = true;
  eventInfoX.value = x;
  eventInfoY.value = y;
}

function hideEventInfo() {
  eventInfo.value = false;
}

function showColorCodingInfo(event) {
  colorCodingInfo.value = true;
  colorCodingInfoX.value = event.clientX;
  colorCodingInfoY.value = event.clientY;
}

function hideColorCodingInfo() {
  colorCodingInfo.value = false;
}

function openReplacementModal(event) {
  currentEvent.value = event;
  selectedReplacementId.value = event.replacement ? event.replacement.id : '';
  showReplacementModal.value = true;
}

function closeReplacementModal() {
  showReplacementModal.value = false;
  currentEvent.value = null;
  selectedReplacementId.value = '';
}

async function updateEventReplacement() {
  if (currentEvent.value) {
    const newReplacementId = selectedReplacementId.value
      ? parseInt(selectedReplacementId.value)
      : null;
    const success = await eventsStore.editEvent(
      currentEvent.value.id,
      newReplacementId
    );
    if (success) {
      closeReplacementModal();
      await usersStore.fetchUsers();
    }
  }
}

// Scroll function
const scrollToCurrentDay = () => {
  if (calendarView.value && calendarView.value.scrollToCurrentDay) {
    calendarView.value.scrollToCurrentDay();
  }
};

// Function to handle mouse down event
function handleMouseDown(e) {
  isDragging.value = true;
  startX.value = e.pageX - calendarContainer.value.offsetLeft;
  scrollLeft.value = calendarContainer.value.scrollLeft;
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
}

// Function to handle mouse move event
function handleMouseMove(e) {
  if (!isDragging.value) return;
  e.preventDefault();
  const x = e.pageX - calendarContainer.value.offsetLeft;
  const walk = (x - startX.value) * 3; // Adjust the multiplier for faster/slower scrolling
  calendarContainer.value.scrollLeft = scrollLeft.value - walk;
}

// Function to handle mouse up event
function handleMouseUp() {
  isDragging.value = false;
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
}

// Lifecycle hooks
onMounted(() => {
  nextTick(() => {
    scrollToCurrentDay();
  });

  if (calendarContainer.value) {
    calendarContainer.value.addEventListener('mousedown', handleMouseDown);
  }
});

onUnmounted(() => {
  if (calendarContainer.value) {
    calendarContainer.value.removeEventListener('mousedown', handleMouseDown);
  }
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
});

// Watch for changes in currentDate
watch(currentDate, (newDate, oldDate) => {
  if (newDate.getFullYear() !== oldDate.getFullYear() || newDate.getMonth() !== oldDate.getMonth()) {
    nextTick(() => {
      if (calendarView.value && calendarView.value.scrollToCurrentDay) {
        calendarView.value.scrollToCurrentDay();
      }
    });
  }
});

</script>